'use client';

import { authClient } from '@/lib/better-auth-client-actions';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  AuthFormHeading,
  AUTH_FORM_SPACING,
  AUTH_PRIMARY_BUTTON,
  AuthFormError,
} from './auth-form-presentation';
import { ButtonLoader } from '@/components/loaders';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { PasswordInput } from '@/components/ui/password-input';
import { handleLocalizedAuthError } from '@/lib/auth/localized-auth-error';
import { getAuthPageCopy } from '@/i18n/auth-page-copy';
import { Mail } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { toast } from 'sonner';
import {
  createPasswordResetRequestSchema,
  createPasswordResetSchema,
  type PasswordResetFormValues,
  type PasswordResetRequestForm,
} from './password-reset-form-schema';

interface PasswordResetFormProps {
  onBackToSignIn: () => void;
  authBasePath?: string;
}

export function PasswordResetForm({
  onBackToSignIn,
  authBasePath = '/auth',
}: PasswordResetFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const copy = getAuthPageCopy(locale);
  const handleAuthError = (error: unknown) =>
    handleLocalizedAuthError(error, locale);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stepParam = searchParams.get('resetStep');
  const step: 'request' | 'reset' = stepParam === 'otp' ? 'reset' : 'request';
  const emailParam = searchParams.get('email') || '';
  const [email, setEmail] = useState(emailParam);
  const [otpCode, setOtpCode] = useState('');
  const isOtpCodeComplete = otpCode.length === 6;
  const otpContainerRef = useRef<HTMLDivElement | null>(null);

  const requestForm = useForm<PasswordResetRequestForm>({
    resolver: zodResolver(createPasswordResetRequestSchema(copy)),
    defaultValues: { email: '' },
  });

  const resetForm = useForm<PasswordResetFormValues>({
    resolver: zodResolver(createPasswordResetSchema(copy)),
    defaultValues: { password: '', confirmPassword: '' },
  });

  React.useEffect(() => {
    if (step === 'reset') {
      setTimeout(() => {
        const input = otpContainerRef.current?.querySelector('input');
        input?.focus();
      }, 0);
    }
  }, [step]);

  React.useEffect(() => {
    if (emailParam && emailParam !== email) {
      setEmail(emailParam);
    }
  }, [emailParam, email]);

  const handleRequest = async (values: PasswordResetRequestForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email: values.email,
        type: 'forget-password',
      });

      if (result.error) {
        const { error: errorMessage, fieldErrors } = handleAuthError(
          result.error
        );
        setError(errorMessage || 'Failed to send reset code');
        if (fieldErrors.email) {
          requestForm.setError('email', { message: fieldErrors.email });
        }
        toast.error(errorMessage || 'Failed to send reset code');
        setIsLoading(false);
        return;
      }

      setEmail(values.email);
      const params = new URLSearchParams(searchParams.toString());
      params.set('mode', 'reset');
      params.set('resetStep', 'otp');
      params.set('email', values.email);
      router.push(`${authBasePath}?${params.toString()}`);
      toast.success('Reset code sent! Check your email');
    } catch (err) {
      const { error: errorMessage, fieldErrors } = handleAuthError(err);
      setError(errorMessage || 'Failed to send reset code');
      if (fieldErrors.email) {
        requestForm.setError('email', { message: fieldErrors.email });
      }
      toast.error(errorMessage || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (values: PasswordResetFormValues) => {
    setIsLoading(true);
    setError(null);

    try {
      if (!isOtpCodeComplete) {
        setError('Enter the 6-digit code');
        setIsLoading(false);
        return;
      }
      const result = await authClient.emailOtp.resetPassword({
        email,
        otp: otpCode,
        password: values.password,
      });

      if (result.error) {
        const { error: errorMessage, fieldErrors } = handleAuthError(
          result.error
        );
        setError(errorMessage || 'Failed to reset password');
        if (fieldErrors && fieldErrors.password) {
          resetForm.setError('password', { message: fieldErrors.password });
        }
        toast.error(errorMessage || 'Failed to reset password');
        setIsLoading(false);
        return;
      }

      toast.success('Password reset successful. Signing you in...');
      const signIn = await authClient.signIn.email({
        email,
        password: values.password,
      });
      if (signIn.error) {
        toast.success(
          'Password reset successful. Please sign in with your new password.'
        );
        const params = new URLSearchParams(searchParams.toString());
        params.set('mode', 'signin');
        params.delete('resetStep');
        params.delete('email');
        router.replace(`${authBasePath}?${params.toString()}`);
        return;
      }
      router.push('/auth/signing-in');
    } catch (err) {
      const { error: errorMessage, fieldErrors } = handleAuthError(err);
      setError(errorMessage || 'Failed to reset password');
      if (fieldErrors && fieldErrors.password) {
        resetForm.setError('password', { message: fieldErrors.password });
      }
      toast.error(errorMessage || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await authClient.emailOtp.sendVerificationOtp({
        email,
        type: 'forget-password',
      });
      if (result.error) {
        const { error: errorMessage } = handleAuthError(result.error);
        setError(errorMessage || 'Failed to resend code');
        toast.error(errorMessage || 'Failed to resend code');
      } else {
        toast.success('Code resent');
      }
    } catch (err) {
      const { error: errorMessage } = handleAuthError(err);
      setError(errorMessage || 'Failed to resend code');
      toast.error(errorMessage || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'reset') {
    return (
      <div className="w-full min-w-0">
        <div>
          <AuthFormHeading
            title={<>Check your email</>}
            description={
              <>
                We sent a reset code to{' '}
                <span className="font-medium">{email}</span>
              </>
            }
          />
        </div>
        <div>
          <Form {...resetForm}>
            <form
              onSubmit={resetForm.handleSubmit(handleReset)}
              className={AUTH_FORM_SPACING}
            >
              <div className="flex flex-col items-center">
                <label className="text-center text-sm font-medium">
                  Reset code
                </label>
                <div ref={otpContainerRef} className="w-full">
                  <InputOTP
                    maxLength={6}
                    autoFocus
                    containerClassName="justify-center"
                    value={otpCode}
                    onChange={setOtpCode}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <FormField
                control={resetForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder="Create a new password"
                        autoComplete="new-password"
                        disabled={isLoading}
                        className="text-base"
                        toggleClassName="text-muted-foreground hover:text-foreground"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={resetForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        placeholder="Confirm your new password"
                        autoComplete="new-password"
                        disabled={isLoading}
                        className="text-base"
                        toggleClassName="text-muted-foreground hover:text-foreground"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <AuthFormError>{error}</AuthFormError>

              <Button
                type="submit"
                className={AUTH_PRIMARY_BUTTON}
                disabled={isLoading || !isOtpCodeComplete}
              >
                <ButtonLoader isLoading={isLoading} loadingText="Resetting...">
                  Reset password
                </ButtonLoader>
              </Button>

              <div className="mt-4 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={handleResendCode}
                  className="text-primary text-sm font-medium hover:underline"
                  disabled={isLoading}
                >
                  Resend code
                </button>
                <span className="text-muted-foreground text-sm">•</span>
                <button
                  type="button"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams.toString());
                    params.set('mode', 'signin');
                    params.delete('resetStep');
                    params.delete('email');
                    router.replace(`${authBasePath}?${params.toString()}`);
                    onBackToSignIn();
                  }}
                  className="text-primary text-sm font-medium hover:underline"
                  disabled={isLoading}
                >
                  Back to sign in
                </button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0">
      <div>
        <AuthFormHeading
          title={<>Reset your password</>}
          description={
            <>
              Enter your email and we will send you a code to reset your
              password
            </>
          }
        />
      </div>
      <div>
        <Form {...requestForm}>
          <form
            onSubmit={requestForm.handleSubmit(handleRequest)}
            className={AUTH_FORM_SPACING}
          >
            <FormField
              control={requestForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <InputGroup>
                      <InputGroupInput
                        {...field}
                        type="email"
                        placeholder="you@example.com"
                        autoComplete="email"
                        disabled={isLoading}
                        className="text-base"
                      />
                      <InputGroupAddon align="inline-end">
                        <Mail className="text-muted-foreground" />
                      </InputGroupAddon>
                    </InputGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AuthFormError>{error}</AuthFormError>

            <Button
              type="submit"
              className={AUTH_PRIMARY_BUTTON}
              disabled={isLoading}
            >
              <ButtonLoader isLoading={isLoading} loadingText="Sending...">
                Send reset code
              </ButtonLoader>
            </Button>

            <div className="mt-4 text-center text-sm">
              Remembered your password?{' '}
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams.toString());
                  params.set('mode', 'signin');
                  params.delete('resetStep');
                  params.delete('email');
                  router.replace(`${authBasePath}?${params.toString()}`);
                  onBackToSignIn();
                }}
                className="text-primary font-medium hover:underline"
                disabled={isLoading}
              >
                Back to sign in
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
